import axios, { type AxiosInstance } from 'axios';
import { route as laravelRoute } from 'ziggyRoute';
import { clientApi } from '@phoenix/clientApi';
import type { LegacyCart, NewComCart } from './types';

const LOCALE_STORAGE_KEY = 'phoenix-newcom';

function hasLineItems(value: unknown): value is NewComCart {
  return !!value && Array.isArray((value as NewComCart).lineItems);
}

function extractCart(payload: unknown): NewComCart | null {
  if (hasLineItems(payload)) {
    return payload;
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    if (hasLineItems(record.cart)) {
      return record.cart;
    }

    if (hasLineItems(record.data)) {
      return record.data;
    }
  }

  return null;
}

class NewComClient {
  private client: AxiosInstance;
  private localePromise: Promise<void> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: window.Phoenix.config.NEWCOM_URL || '',
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  getLocale(): string {
    return window.Phoenix.localisationUrlCode.split('-').reverse().join('-');
  }

  ensureLocaleCookies(): Promise<void> {
    if (this.localePromise) {
      return this.localePromise;
    }

    const guard = this.getLocale();

    if (window.sessionStorage.getItem(LOCALE_STORAGE_KEY) === guard) {
      this.localePromise = Promise.resolve();
      return this.localePromise;
    }

    this.localePromise = this.client
      .post('/api/commerce/setLocaleCookies', { locale: this.getLocale() })
      .then(() => {
        window.sessionStorage.setItem(LOCALE_STORAGE_KEY, guard);
      })
      .catch((error) => {
        this.localePromise = null;
        throw error;
      });

    return this.localePromise;
  }

  async addCartLineItem(reference: string, quantity = 1): Promise<LegacyCart> {
    await this.ensureLocaleCookies();

    // We manually build the SKU because a reference can start with the prefix itself.
    // Eg. IW328207 -> IWIW328207, LS328207 -> LSLS328207
    const prefix = window.Phoenix.config.MAIN_PRODUCT_PREFIX;
    const sku = `${prefix}${reference}`;

    const { data } = await this.client.post('/api/commerce/addCartLineItem', {
      sku: sku,
      quantity,
    });

    return this.enrichCart(NewComClient.assertCart(data));
  }

  async getCart(): Promise<LegacyCart> {
    const { data } = await this.client.post('/api/commerce/tryCustomGetMe', {});

    return this.enrichCart(extractCart(data));
  }

  async removeCartLineItem(lineItemId: string): Promise<LegacyCart> {
    const { data } = await this.client.post('/api/commerce/removeCartLineItem', { lineItemId });

    return this.enrichCart(NewComClient.assertCart(data));
  }

  private async enrichCart(cart: NewComCart | null): Promise<LegacyCart> {
    if (cart === null) {
      return {
        items: [],
        total: '',
        sub_total: '',
        total_tax: '',
        vatInfo: '',
        country_code: window.Phoenix.currentCountry,
        tax_included: false,
        taxIncluded: false,
      };
    }

    const { data } = await clientApi.post(laravelRoute('api.cart.newcom.enrich'), { cart });

    return data.data.cart;
  }

  private static assertCart(payload: unknown): NewComCart {
    const cart = extractCart(payload);

    if (cart === null) {
      const error = (payload ?? {}) as { reason?: string; details?: string };
      throw new Error(error.reason || error.details || '');
    }

    return cart;
  }
}

export const newComClient = new NewComClient();
